import React, { useEffect, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Header } from '../../components';
import { customStyles } from '../../Utils/utils';
import { Box, Paper, Typography, Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { useNavigate } from 'react-router-dom';
import { getReportTemplatesApi } from '../../rtk-apis/reports/getReportTemplates';
import { clearState } from '../../store/slices/reports/getReportTemplates';

// Minimalist line icons (match the auth/login set)
const PulseIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12h3l2.5-6 4 13 2.5-7H21" />
  </svg>
);
const StressIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 13h3l2-5 3 9 2.5-6H18" />
    <path d="M16 5l4-2-2 4" />
    <path d="M20 3c-1 5-3 7-6 8.5" />
  </svg>
);

const isStress = (name = '') => /stress/i.test(name);

const PAPER = '#cdd9e8';
const PAPER_SOFT = '#dde5f1';

/** A small "document" that reads like a real cardiac report (header, title, meta,
 *  a section + body lines, a mini measurement grid, sign-off) — not a grey skeleton. */
const ReportThumb = ({ accent }: { accent: string }) => {
  const bar = (w: number | string, h = 5, c: string = PAPER) => (
    <Box
      sx={{
        width: typeof w === 'number' ? w : `${w}%`,
        height: h,
        borderRadius: 0.6,
        backgroundColor: c,
      }}
    />
  );
  return (
    <Box
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'linear-gradient(180deg,#fbfdff 0%,#eef3f9 100%)',
        border: '1px solid #d7e0ec',
        boxShadow: '0 10px 26px -16px rgba(0,0,0,.75)',
        p: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.8,
        aspectRatio: '1 / 0.9',
      }}
    >
      {/* letterhead */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 15, height: 15, borderRadius: '50%', background: accent }} />
          {bar(60, 7, '#9fb0c6')}
        </Box>
        {bar(30, 6, '#c2cfdf')}
      </Box>
      {/* report title */}
      <Box
        sx={{
          width: '54%',
          height: 9,
          borderRadius: 0.8,
          backgroundColor: accent,
          opacity: 0.9,
          mt: 0.6,
        }}
      />
      <Box sx={{ height: '1px', backgroundColor: '#dde5f0', my: 0.4 }} />
      {/* patient meta — two columns */}
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {[0, 1].map(c => (
          <Box
            key={c}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            {bar(72, 4, PAPER_SOFT)}
            {bar(90, 4, PAPER_SOFT)}
          </Box>
        ))}
      </Box>
      {/* section heading + body */}
      <Box
        sx={{
          width: '32%',
          height: 6,
          borderRadius: 0.6,
          backgroundColor: accent,
          opacity: 0.5,
          mt: 0.6,
        }}
      />
      {bar(96)}
      {bar(88)}
      {/* mini measurement grid */}
      <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '3px' }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 10,
              borderRadius: '3px',
              border: '1px solid #e6ecf4',
              backgroundColor: i % 4 === 0 ? `${accent}22` : '#e3eaf3',
            }}
          />
        ))}
      </Box>
      <Box sx={{ flex: 1 }} />
      {/* sign-off */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{bar(38, 5, '#cdd8e7')}</Box>
    </Box>
  );
};

const TemplateListV2 = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, result, loading } = useSelector(
    (state: RootState) => state.getReportTemplatesResponse
  );

  useEffect(() => {
    dispatch(getReportTemplatesApi({ search: '' }));
    return () => {
      clearState();
    };
  }, []);

  useEffect(() => {
    if (status === 'SUCCESS' && !loading) {
      setTemplates(result?.data || []);
    }
  }, [status, loading]);

  return (
    <section
      className="layoutContainer"
      style={customStyles.layoutBackground}
    >
      <div className="layoutGrid">
        <div className="page">
          <Header />
          <div className="page__body">
            <Box
              sx={{
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                maxWidth: 1040,
                mx: 'auto',
                width: '100%',
                px: { xs: 2, md: 3 },
                py: 3,
                color: '#e7eef7',
              }}
            >
              {/* command bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  mb: 3.5,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      color: '#2dd4bf',
                      fontWeight: 700,
                    }}
                  >
                    Reporting
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Spectral',serif",
                      fontSize: 28,
                      fontWeight: 600,
                      color: '#e9f0f8',
                      lineHeight: 1.1,
                    }}
                  >
                    Report Templates
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5 }}>
                    <b style={{ color: '#2dd4bf' }}>{templates.length}</b> template
                    {templates.length === 1 ? '' : 's'} available
                  </Typography>
                </Box>
                <Button
                  onClick={() => navigate('/uploadtemplate')}
                  variant="contained"
                  disableElevation
                  startIcon={
                    <Box
                      component="span"
                      sx={{ fontSize: 18, lineHeight: 1, mr: -0.25 }}
                    >
                      ＋
                    </Box>
                  }
                  sx={{
                    height: 44,
                    px: 2.5,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#04221d',
                    background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
                    boxShadow: '0 8px 24px -10px rgba(45,212,191,.6)',
                    '&:hover': { background: 'linear-gradient(90deg,#0f9e8e,#26c2ad)' },
                  }}
                >
                  Upload Template
                </Button>
              </Box>

              {/* states */}
              {loading && !templates.length && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    color: '#9fb2cc',
                    py: 6,
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress
                    size={22}
                    sx={{ color: '#2dd4bf' }}
                  />
                  Loading templates…
                </Box>
              )}
              {!loading && !templates.length && (
                <Box sx={{ textAlign: 'center', color: '#6a7e9c', py: 8 }}>
                  <Box sx={{ fontSize: 40, mb: 1 }}>🗎</Box>
                  <Typography sx={{ color: '#9fb2cc', fontSize: 15 }}>No templates yet</Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Upload a report template to get started.
                  </Typography>
                </Box>
              )}

              {/* template cards */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(300px, 1fr))' },
                  gap: 3,
                }}
              >
                {templates.map(t => {
                  const stress = isStress(t?.templateName);
                  const accent = stress ? '#a78bfa' : '#2dd4bf';
                  const accentSoft = stress ? 'rgba(167,139,250,.13)' : 'rgba(45,212,191,.13)';
                  const btnBg = stress
                    ? 'linear-gradient(90deg,#7c5cf0,#a78bfa)'
                    : 'linear-gradient(90deg,#14b8a6,#2dd4bf)';
                  return (
                    <Paper
                      key={t?._id}
                      elevation={0}
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(180deg,#102338 0%,#0c1a2b 100%)',
                        border: '1px solid #243652',
                        borderRadius: '18px',
                        p: 2.5,
                        pt: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        transition:
                          'border-color .16s ease, transform .1s ease, box-shadow .16s ease',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: `linear-gradient(90deg, ${accent}, transparent 80%)`,
                        },
                        '&:hover': {
                          borderColor: accent,
                          transform: 'translateY(-4px)',
                          boxShadow: `0 20px 44px -20px ${accent}80`,
                        },
                      }}
                    >
                      {/* header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            borderRadius: '12px',
                            display: 'grid',
                            placeItems: 'center',
                            color: accent,
                            backgroundColor: accentSoft,
                            border: `1px solid ${accent}`,
                          }}
                        >
                          {stress ? <StressIcon /> : <PulseIcon />}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: "'Spectral',serif",
                              fontSize: 19,
                              fontWeight: 600,
                              color: '#e9f0f8',
                              lineHeight: 1.15,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={t?.templateName}
                          >
                            {t?.templateName}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#6a7e9c' }}>
                            Default report layout
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flexShrink: 0,
                            px: 1.1,
                            py: 0.4,
                            borderRadius: '999px',
                            fontSize: 10.5,
                            fontWeight: 700,
                            letterSpacing: '0.08em',
                            color: accent,
                            backgroundColor: accentSoft,
                            border: `1px solid ${accent}55`,
                          }}
                        >
                          {stress ? 'STRESS' : 'ECHO'}
                        </Box>
                      </Box>

                      <ReportThumb accent={accent} />

                      <Button
                        onClick={() =>
                          navigate(`./preview/${t?._id}`, {
                            state: { _id: t?._id, editorContent: t?.templateHTML },
                          })
                        }
                        fullWidth
                        disableElevation
                        sx={{
                          height: 46,
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontSize: 14.5,
                          fontWeight: 700,
                          color: stress ? '#fff' : '#04221d',
                          background: btnBg,
                          boxShadow: `0 8px 22px -10px ${accent}99`,
                          '&:hover': { filter: 'brightness(1.06)', background: btnBg },
                        }}
                      >
                        Preview template
                      </Button>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TemplateListV2;
